import FieldModel from "../models/FieldModel.js";
import Utils from "../utils/Common.util.js";
import ValidationUtils from "../utils/Validation.util.js";
import { MESSAGE_KEY_CUSTOM_HTML_WIDGET, MESSAGE_KEY_CUSTOM_VALIDATE_MESSAGE, MESSAGE_KEY_SET_FIELD_VALUE } from "../../core/const/Constant.js";
import { useSubscribeDOMRendered } from "../../core/hook/useSubcribeDOMRendered.js";
import { useSubcribeDOMDestroyed } from "../../core/hook/useSubcribeDOMDestroyed.js";
import { useDOMEvent } from "../hook/useDOMEvent.js";
import { useSubcribeWindowMessage } from "../hook/useSubcribeWindowMessage.js";
import { useMessageValidation } from "../hook/useMessageValidation.js";
import useReactiveZone from "reactive-zone";

// Using Symbol as private variable, 
// The child cannot access those variables
const PRIVATE = Symbol('private'),
    PRIVATE_DO_INIT = Symbol('init'),
    PRIVATE_DO_RENDER = Symbol('render'),
    PRIVATE_ON_VALUE_CHANGED = Symbol('valueChanged'),
    PRIVATE_BIND_ON_VALUE_CHANGED = Symbol('bindValueChanged'),
    PRIVATE_ON_RENDERED = Symbol('onRendered'),
    PRIVATE_ON_DESTROYED = Symbol('onDetroyed'),
    PRIVATE_ON_FIELD_MODEL_CHANGED = Symbol('onFieldModelChanged'),
    PRIVATE_ON_FIELD_MODEL_PROPERTY_CHANGED = Symbol('onFieldModelChanged'),
    PRIVATE_SET_DEFAULT_VALUE = Symbol('setFieldDefaultValue'),
    PRIVATE_DEFINE_REACTIVE = Symbol('injectReactive'),
    PRIVATE_REACTIVE_ZONE = Symbol('reactiveZone')


/**
 * 
 * @param {TaiBaseWidget} widget 
 */
const useSubcribeSetFieldValue = (widget) => {
    return useSubcribeWindowMessage(MESSAGE_KEY_SET_FIELD_VALUE, ({ fieldCode = '', value = '' }) => {
        if (widget.fieldModel.code === fieldCode) {
            widget.setValue(value);
        }
    })
}

const getCustomHTMLWidget = (() => {

    const store = new Map();

    useSubcribeWindowMessage(MESSAGE_KEY_CUSTOM_HTML_WIDGET, ({ formId, customField }) => {
        store.set(formId, customField);
    })

    return (formId, fieldCode) => {
        if (store.has(formId)) {
            return store.get(formId)[fieldCode];
        }
    }
})();

class TaiBaseWidget {

    constructor(fieldModel) {
        if (!fieldModel) {
            throw new Error('fieldModel not allow null');
        }

        this[PRIVATE].fieldModel = fieldModel;
        this.doValidate = this.doValidate.bind(this);
        this._();
        this.call(PRIVATE_DO_INIT)
    }

    /**@type {ITaiBaseWidgetMeta} */
    [PRIVATE] = {
        id: Utils.GenerateUniqueID(),
        fieldModel: new FieldModel(),
        inputControls: [],
        fragment: document.createDocumentFragment(),
        selfRoot: null,
    };

    instance = this

    /**
     *
     * @readonly
     * @returns {FieldModel}
     */
    get fieldModel() {
        return this[PRIVATE].fieldModel;
    }

    /**
     *
     *
     * @readonly
     * @memberof TaiBaseWidget
     * @returns {string | number}
     */
    get id() {
        return this[PRIVATE].id;
    }

    /**
     *
     * @param {FieldModel} model
     * @memberof TaiBaseWidget
     */
    set fieldModel(model) {
        const fieldModel = this[PRIVATE].fieldModel;
        Object.assign(fieldModel, model);
    }

    /**
     *
     *
     * @readonly
     * @memberof TaiBaseWidget
     * @return {HTMLElement | null}
     */
    get selfRoot() {
        return this[PRIVATE].selfRoot
    }

    /** 
     * @returns { Array<HTMLElement>}
     */
    get inputControls() {
        return this[PRIVATE].inputControls;
    }

    [PRIVATE_DO_INIT]() {

        this.onInit();
        this.call(PRIVATE_DO_RENDER)
    }

    [PRIVATE_DO_RENDER]() {
        let html = '', customHTML = getCustomHTMLWidget(this.fieldModel.formUUID, this.fieldModel.code);
        if (customHTML) {
            html = customHTML;
        } else if (this.fieldModel.htmlTemplate && this.fieldModel.htmlTemplate.length > 0) {
            html = this.fieldModel.htmlTemplate;
        } else {
            html = this.template()
        }
        const elm = Utils.HtmlToDom(html);
        // const elm = Utils.HtmlToDom(this.template());
        this[PRIVATE].selfRoot = elm;

        // const html = elm.innerHTML;
        // elm.innerHTML = '';

        const { selfRoot } = this[PRIVATE]

        const { useTemplate, onZoneReady, injectPropToZone, onZoneInit, injectMethodToZone } = useReactiveZone(this);

        const props = injectPropToZone({
            fieldModel: this[PRIVATE].fieldModel,
        });

        injectMethodToZone({
            doValidate: this.doValidate.bind(this)
        })

        onZoneInit(this.onZoneInit.bind(this))

        useTemplate({
            applyRoot: this.selfRoot
        })

        this[PRIVATE].fieldModel = props.fieldModel;


        onZoneReady(() => {
            this.onZoneReady();
            this.call(PRIVATE_ON_RENDERED);
        });


        useSubscribeDOMRendered(selfRoot, () => {

            // this.call(PRIVATE_ON_RENDERED);
            this.call(PRIVATE_BIND_ON_VALUE_CHANGED);
            useSubcribeSetFieldValue(this);

        });

        useSubcribeDOMDestroyed(selfRoot, () => {
            this.call(PRIVATE_BIND_ON_VALUE_CHANGED)
            this.call(PRIVATE_ON_DESTROYED)
        })
    }

    [PRIVATE_ON_RENDERED]() {
        const { uuid, code } = this.fieldModel;
        this.selfRoot?.setAttribute('data-tai-id', uuid);
        this.selfRoot?.setAttribute('data-tai-code', code);
        this[PRIVATE].inputControls = Array.from(this.querySelectorAll(`[name=${code}]`));
        this[PRIVATE].inputControls['first'] = () => {
            return this[PRIVATE].inputControls[0];
        };
        this.call(PRIVATE_SET_DEFAULT_VALUE);

        const elmLabel = this.querySelector('.tai-label');
        if (!this.fieldModel.showLabel && elmLabel) {
            elmLabel.style.visibility = 'hidden';
            elmLabel.style.opacity = '0';
        }

        this.onRendered();
    }

    [PRIVATE_ON_DESTROYED]() {
        this.onDestroyed()
    }

    [PRIVATE_ON_VALUE_CHANGED]() {
        this.onValueChanges(this.getValue());
    }

    [PRIVATE_BIND_ON_VALUE_CHANGED]() {
        useDOMEvent(this.selfRoot, 'change', (e) => {
            if (this.disableAutoDetectInputChanges()) {
                return;
            }
            this.doValidate();
            this.fieldModel.userResponse = this.getValue();
            this.call(PRIVATE_ON_VALUE_CHANGED);
        });

        useDOMEvent(this.selfRoot, "blur", (e) => {
            this.doValidate();
        }, { capture: true })
    }

    [PRIVATE_ON_FIELD_MODEL_CHANGED]() {
        this.onFieldModelChanged();
    }

    [PRIVATE_ON_FIELD_MODEL_PROPERTY_CHANGED](prop, value) {
        if (prop === 'errorMsg' && value) {
            this.setErrorMessage(value);
        }
        this.onFieldModelPropChanged(prop, value);
    }

    [PRIVATE_SET_DEFAULT_VALUE]() {
        const { code, userResponse } = this.fieldModel
        const defaultValue = Utils.QueryUrlParameter('tai-' + code);
        const isable = defaultValue && this.fieldModel.canSetValue(defaultValue)
        if (userResponse !== undefined || userResponse !== null || userResponse !== '') {
            this.setValue(userResponse);
            return;
        }
        if (isable) {
            this.fieldModel.userResponse = defaultValue;
            this.setValue(defaultValue);
        } else {
            this.fieldModel.userResponse = this.getValue();
        }
    }

    /**
     * Generate the html
     * @abstract
     * @returns {string}
     */
    template() {
        return '<div></div>'
    }


    /**
     * Do validation that user input, return true if the input valid, otherwise is false
     * @returns {boolean}
     */
    doValidate() {
        const { validations, type, code } = this.fieldModel;
        const value = this.getValue();
        for (let i = 0; i < validations.length; i++) {
            const { rule, format } = validations[i];
            try {
                const isValid = this.onValidateRule(rule, value, format);
                if (!isValid) {
                    const msg = useMessageValidation(type, code, validations[i]);
                    this.setErrorMessage(msg);
                    return false;
                } else {
                    this.fieldModel.userResponse = value;
                }
            } catch (ex) {
                console.error(ex);
            }
        }
        this.setErrorMessage('');
        return true;
    }

    /**
     * Do validate 
     * @param {strinemaig} rule 
     * @param {any} value 
     * @param {string} format 
     * @returns {boolean}
     */
    onValidateRule(rule, value, format) {
        rule = rule.toLowerCase();
        if (!ValidationUtils[rule]) {
            console.error(`Missing "${rule}" rule!!`);
            return false;
        }
        return ValidationUtils[rule].call(null, value, format)
    }

    /**
     * 
     * @param {string} selector 
     * @returns {HTMLElement | null}
     */
    querySelector(selector) {
        return this.selfRoot.querySelector(selector) || document.createElement('div');
    }

    /**
     * 
     * @param {string} selector 
     * @returns {NodeListOf< HTMLElement>}
     */
    querySelectorAll(selector) {
        return this.selfRoot.querySelectorAll(selector);
    }

    /**
     * 
     * @returns {HTMLElement}
     */
    getDOM() {
        return this.selfRoot;
    }

    getValue() {
        const { inputControls } = this[PRIVATE];
        let value;
        if (inputControls.length == 1) {
            value = inputControls[0].value;
        } else if (inputControls.length > 1) {
            value = inputControls.map(elm => elm.value);
        }
        return value;
    }

    setValue(value) {

    }

    setErrorMessage(message = '') {
        this.fieldModel.errorMsg = message;
        if (message.length > 0) {
            this.selfRoot.classList.add('is-error');
        } else {
            this.selfRoot.classList.remove('is-error');
        }
        // this.querySelector('.tai-error .f-msg').textContent = message;
    }

    clearValue() {
        this.querySelectorAll('.tai-control').forEach(item => item.value = '');
    }

    /**
     * 
     * @returns {IUserResponse}
     */
    toUserResponse() {
        return {
            "code": this.fieldModel.code,
            "value": this.getValue()
        }
    }

    scrollIntoView() {
        this.selfRoot?.scrollIntoView();
    }

    /**
     * @description Allow auto detect user input 
     * @returns {boolean}
     */
    disableAutoDetectInputChanges() {
        return false;

    }

    call(methodName, args, ...rest) {
        const fn = this[methodName];
        if (typeof fn === 'function') {
            return fn.call(this, args, ...rest);
        } else {
            console.error(`Method "${methodName}" does not exist!!`);
        }
    }

    /**
     * @abstract
     */
    onInit() {

    }

    /**
     * @abstract
     */
    onRendered() { }

    _() { }


    onZoneInit() {

    }

    onZoneReady() { }

    /**
     * @abstract
     */
    onDestroyed() { }

    /**
     * When the fieldModel changed, this function will be trigger
     * This will be called after onFieldModelPropChanged 
     * @abstract
     */
    onFieldModelChanged() { }

    /**
     * When the property of fieldModel changed, this function will be trigger
     * @abstract
     * @param {string} prop 
     * @param {any} value 
     */
    onFieldModelPropChanged(prop, value) { }

    /**
     * When the value of input (input, select, textarea...) changed, this function will be trigger
     * @abstract
     */
    onValueChanges(value) { }

    [Symbol.toPrimitive]() {
        return 'widget-' + this[PRIVATE].id;
    }
}

export default TaiBaseWidget
